import i18n from '@/i18n';
/**
 * 素材采集批次：图片空间异步同步进度（imageSyncDone / imageSyncTotal）。
 * 状态收口界限见 docs/sourcing-upload-stuck-closure-plan-20260807.md
 */
export function resolveSourcingImageSyncProgress(batch) {
  const total = Number(batch?.imageSyncTotal ?? batch?.image_sync_total ?? 0) || 0;
  const done = Number(batch?.imageSyncDone ?? batch?.image_sync_done ?? 0) || 0;
  const active = total > 0 && done < total;
  const percent = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0;
  return { total, done, active, percent };
}

/** 采集列表进度：条目未齐时优先入库进度；否则图片同步；避免「图同步满条 + 仍上传中」误显示 100% */
export function resolveBatchCollectProgress(batch) {
  const effective = resolveEffectiveCaptureStatus(batch);
  const stored = batch?.captureStatus ?? batch?.capture_status;
  const image = resolveSourcingImageSyncProgress(batch);
  const ingest = resolveBatchIngestProgress(batch);
  const uploading = effective === 'uploading' || stored === 'uploading';

  if (uploading && ingest.itemsIngesting && ingest.total > 0) {
    const percent = Math.min(100, Math.round((ingest.done / ingest.total) * 100));
    return {
      total: ingest.total,
      done: ingest.done,
      active: true,
      percent,
      indeterminate: false,
      label: '入库',
      detail: `${ingest.done}/${ingest.total}`,
    };
  }

  if (image.total > 0) {
    const active = uploading && image.active;
    return {
      ...image,
      active,
      percent: active ? image.percent : 100,
      indeterminate: false,
      label: '图片',
      detail: `${image.done}/${image.total}`,
    };
  }
  const done = ingest.done;
  const total = ingest.total;

  if (uploading && total > 0 && done < total) {
    const percent = Math.min(100, Math.round((done / total) * 100));
    return {
      total,
      done,
      active: true,
      percent,
      indeterminate: false,
      label: '入库',
      detail: `${done}/${total}`,
    };
  }

  // 条目已齐（或无 expected）且尚无 imageSyncTotal，DB 仍 uploading：收尾等待同步记账
  if (stored === 'uploading' && image.total <= 0) {
    return {
      total: total || done,
      done,
      active: true,
      percent: 0,
      indeterminate: true,
      label: '收尾中',
      detail: '等待图片同步',
    };
  }

  if (effective === 'uploading') {
    return {
      total: total || done,
      done,
      active: true,
      percent: 0,
      indeterminate: true,
      label: '收尾中',
      detail: ingest.ingestFinalized ? '等待图片同步' : '处理中',
    };
  }

  return {
    total: 0,
    done: 0,
    active: false,
    percent: 100,
    indeterminate: false,
    label: '',
    detail: '',
  };
}

/** 列表数量列：仅上传中展示 a/b；已完成不因 itemCount 漂移显示 100/120 */
export function formatSourcingBatchItemCount(batch, mode = 'collect') {
  const done = Number(batch?.itemCount ?? batch?.item_count ?? 0) || 0;
  const total = batch?.expectedItemCount ?? batch?.expected_item_count;
  const status = mode === 'collect'
    ? resolveEffectiveCaptureStatus(batch)
    : (batch?.captureStatus ?? batch?.capture_status);
  const parsedTotal = Number(total);
  if (
    mode === 'collect'
    && Number.isFinite(parsedTotal)
    && parsedTotal > 0
    && (status === 'uploading' || (status === 'terminated' && done < parsedTotal))
  ) {
    return `${done}/${parsedTotal}`;
  }
  return String(done);
}

/** 条目入库进度（expected 已满时视为已 finalize，兼容未写 ingestFinalized 的历史批次） */
export function resolveBatchIngestProgress(batch) {
  const done = Number(batch?.itemCount ?? batch?.item_count ?? 0) || 0;
  const total = Number(batch?.expectedItemCount ?? batch?.expected_item_count ?? 0) || 0;
  const explicitFinalized = Boolean(batch?.ingestFinalized ?? batch?.ingest_finalized);
  const itemsIngesting = total > 0 && done < total;
  const itemsComplete = total > 0 && done >= total;
  const ingestFinalized = explicitFinalized || itemsComplete;
  return { done, total, ingestFinalized, itemsIngesting, explicitFinalized, itemsComplete };
}

/** 采集工作是否已全部结束（条目 + 图片同步） */
export function isSourcingCollectWorkComplete(batch) {
  if (!batch) return false;
  const stored = batch?.captureStatus ?? batch?.capture_status;
  if (stored === 'terminated') return false;
  // 后端/视图已 completed：勿因 itemCount 漂移（如 100 < expected 120）否定完成
  if (stored === 'completed') return true;
  const image = resolveSourcingImageSyncProgress(batch);
  const ingest = resolveBatchIngestProgress(batch);
  if (image.active) return false;
  // 显式 finalize 后即视为条目收口完成（计数可能短暂落后）
  if (ingest.explicitFinalized) return true;
  return ingest.ingestFinalized && !ingest.itemsIngesting;
}

/** 展示用采集状态：图片同步与条目入库均完成时视为 completed */
export function resolveEffectiveCaptureStatus(batch) {
  const stored = batch?.captureStatus ?? batch?.capture_status;
  if (stored === 'terminated') return 'terminated';
  const image = resolveSourcingImageSyncProgress(batch);
  if (stored === 'completed') {
    return image.active ? 'uploading' : 'completed';
  }
  if (isSourcingCollectWorkComplete(batch)) return 'completed';
  if (stored === 'uploading') return 'uploading';
  const ingest = resolveBatchIngestProgress(batch);
  if (image.active || ingest.itemsIngesting || !ingest.ingestFinalized) return 'uploading';
  return stored || 'completed';
}

/**
 * 是否仍需轮询批次元数据 / 条目。
 * DB/视图仍为 uploading 时继续轮询，直到 completed/terminated（避免前端自觉完成就停 poll）。
 */
export function shouldPollSourcingBatch(batch) {
  if (!batch) return false;
  const stored = batch?.captureStatus ?? batch?.capture_status;
  if (stored === 'terminated' || stored === 'completed') return false;
  if (stored === 'uploading') return true;
  return !isSourcingCollectWorkComplete(batch);
}

export function isSourcingBatchProcessing(batch) {
  return shouldPollSourcingBatch(batch);
}

/** 采集批次是否可终止（上传/同步进行中） */
export function canTerminateSourcingCaptureBatch(batch) {
  if (!batch) return false;
  const stored = batch?.captureStatus ?? batch?.capture_status;
  if (stored === 'terminated' || stored === 'completed') return false;
  if (resolveEffectiveCaptureStatus(batch) === 'completed') return false;
  return shouldPollSourcingBatch(batch);
}

export function formatSourcingImageSyncDetail(batch) {
  const { total, done, active } = resolveSourcingImageSyncProgress(batch);
  if (total <= 0) return '';
  if (!active && done >= total) return i18n.t('workbench:ipeUtilError.imagesSynced', { done, total });
  return i18n.t('workbench:ipeUtilError.imagesSyncing', { done, total });
}
